import store from '@/redux/store';
import { saveAuthData, logout } from '@/redux/slice/authSlice';
import { showPopupMessage } from '@/redux/slice/popupSlice';
import { authService } from './services/authService';

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: Error) => void }> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error!);
    }
  });
  failedQueue = [];
};

export const setupInterceptors = (axiosClient: any) => {
  // Request Interceptor
  axiosClient.interceptors.request.use(
    (config: any) => {
      const state = store.getState();
      const { token, tenantId, branches } = state.auth;
      const branchId = branches.length > 0 ? branches[0] : null; // Assuming first branch is the active one
      
      if (token && config.url !== 'Account/Authenticate') {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      
      if (tenantId) {
        config.headers['Tenant-Id'] = tenantId;
      }
      
      if (branchId) {
        config.headers['Branch-Id'] = branchId;
      }
      
      return config;
    },
    (error: any) => {
      console.error('Request Error:', error);
      return Promise.reject(new Error(error.message || 'Request failed'));
    }
  );

  // Response Interceptor
  axiosClient.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest = error.config;

      if (!error.response) {
        console.error('Network Error:', error);
        store.dispatch(showPopupMessage('Network error. Please check your connection.'));
        return Promise.reject(new Error('Network error'));
      }

      if (error.response.status === 403) {
        const errorMessage = error.response.data?.message || '';

        if (errorMessage.includes('CONCURRENT_USER_DETECTED') || errorMessage.includes('Session terminated')) {
          store.dispatch(showPopupMessage('Your session has been terminated due to login on another device. Please log in again.'));
          store.dispatch(logout());
          return Promise.reject(new Error('Session terminated due to concurrent login'));
        }
      }

      if (error.response.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axiosClient(originalRequest);
            })
            .catch((err) => Promise.reject(new Error(err.message || 'Request failed')));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const state = store.getState();
        const refreshToken = state.auth.refreshToken;

        if (!refreshToken) {
          console.error('No refresh token available, logging out');
          store.dispatch(showPopupMessage('Session expired. Please log in again.'));
          store.dispatch(logout());
          return Promise.reject(new Error('No refresh token available'));
        }

        try {
          const tokenData = await authService.RefreshTokenService(refreshToken);

          if (!tokenData?.accessToken || !tokenData?.refreshToken) {
            throw new Error('Invalid refresh token response');
          }

          store.dispatch(
            saveAuthData({
              ...state.auth.user,
              token: tokenData.accessToken,
              refreshToken: tokenData.refreshToken,
            })
          );

          processQueue(null, tokenData.accessToken);
          
          originalRequest.headers['Authorization'] = `Bearer ${tokenData.accessToken}`;
          return axiosClient(originalRequest);
        } catch (err: any) {
          console.error('Token Refresh Error:', err.message);
          processQueue(new Error(err.message || 'Failed to refresh token'), null);
          store.dispatch(showPopupMessage('Session expired. Please log in again.'));
          store.dispatch(logout());
          return Promise.reject(new Error(err.message || 'Failed to refresh token'));
        } finally {
          isRefreshing = false;
        }
      }

      console.error('Response Error:', error.message);
      return Promise.reject(error);
    }
  );
};